/**
 * Conflict resolution dialog for optimistic locking conflicts.
 */

import { Modal, Button, Space, Typography, Alert, Descriptions } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { formatDate } from '@/utils';

const { Text } = Typography;

export interface ConflictInfo {
  currentVersion: number;
  serverVersion: number;
  lastModifiedBy?: string;
  lastModifiedAt?: string;
  conflictFields?: Array<{
    field: string;
    yourValue: unknown;
    serverValue: unknown;
  }>;
}

interface ConflictDialogProps {
  open: boolean;
  conflict: ConflictInfo | null;
  onOverwrite: () => void;
  onAbandon: () => void;
  onRefresh: () => void;
  loading?: boolean;
}

export const ConflictDialog: React.FC<ConflictDialogProps> = ({
  open,
  conflict,
  onOverwrite,
  onAbandon,
  onRefresh,
  loading = false,
}) => {
  if (!conflict) return null;

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined className="text-yellow-500" />
          <span>数据冲突</span>
        </Space>
      }
      open={open}
      onCancel={onAbandon}
      footer={null}
      width={600}
    >
      <Alert
        title="该记录已被其他用户修改"
        description="您正在编辑的数据已被其他用户更新。请选择如何处理此冲突。"
        type="warning"
        showIcon
        className="mb-4"
      />

      <Descriptions column={1} bordered size="small" className="mb-4">
        <Descriptions.Item label="您的版本号">
          {conflict.currentVersion}
        </Descriptions.Item>
        <Descriptions.Item label="服务器版本号">
          {conflict.serverVersion}
        </Descriptions.Item>
        {conflict.lastModifiedBy && (
          <Descriptions.Item label="最后修改人">
            {conflict.lastModifiedBy}
          </Descriptions.Item>
        )}
        {conflict.lastModifiedAt && (
          <Descriptions.Item label="最后修改时间">
            {formatDate(conflict.lastModifiedAt)}
          </Descriptions.Item>
        )}
      </Descriptions>

      {conflict.conflictFields && conflict.conflictFields.length > 0 && (
        <div className="mb-4">
          <Text strong className="block mb-2">冲突字段:</Text>
          <div className="space-y-2">
            {conflict.conflictFields.map((field, index) => (
              <div key={index} className="p-2 bg-gray-50 rounded">
                <Text strong>{field.field}:</Text>
                <div className="flex gap-4 mt-1">
                  <div className="flex-1">
                    <Text type="secondary" className="text-xs">您的值:</Text>
                    <div className="text-red-500 line-through">
                      {JSON.stringify(field.yourValue)}
                    </div>
                  </div>
                  <div className="flex-1">
                    <Text type="secondary" className="text-xs">服务器值:</Text>
                    <div className="text-green-500">
                      {JSON.stringify(field.serverValue)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button onClick={onAbandon}>
          放弃修改
        </Button>
        <Button onClick={onRefresh}>
          刷新查看最新
        </Button>
        <Button
          type="primary"
          danger
          onClick={onOverwrite}
          loading={loading}
        >
          强制覆盖
        </Button>
      </div>
    </Modal>
  );
};

export default ConflictDialog;
